﻿/**
 * Relógio do servidor visto pelo navegador, para o cronômetro das rodadas online.
 *
 * Antes, o horário do servidor era calculado a cada resposta como "hora que o servidor mandou + tempo local desde que chegou",
 * ignorando o tempo que a resposta levou para chegar. Como esse atraso varia a cada resposta (principalmente por ngrok), o
 * cronômetro dava pequenos saltos para a frente e para trás sempre que chegava uma visão nova: parecia travado.
 *
 * Agora cada resposta vira uma amostra da diferença entre o relógio do servidor e o local. A melhor amostra é a de menor atraso
 * (a maior diferença), então ficamos com o máximo do último minuto. A diferença usada é puxada para esse valor aos
 * poucos (no máximo 5% do tempo que passou), e o horário devolvido nunca anda para trás.
 */

interface Amostra {
  t: number;
  diferenca: number;
}

const JANELA_MS = 60_000;
const VELOCIDADE_MAX_AJUSTE = 0.05;

let amostras: Amostra[] = [];
let alvo: number | null = null;
let aplicada = 0;
let ultimaLeitura = 0;
let ultimoValor = 0;

/** Esquece tudo (ao entrar numa sala ou sair dela). */
export function reiniciarRelogioServidor(): void {
  amostras = [];
  alvo = null;
  aplicada = 0;
  ultimaLeitura = 0;
  ultimoValor = 0;
}

/** Registra o horário do servidor que veio numa resposta, recebida agora (`recebidoEm`, relógio local). */
export function registrarAmostra(servidorAgora: number, recebidoEm: number): void {
  amostras.push({ t: recebidoEm, diferenca: servidorAgora - recebidoEm });
  while (amostras.length > 1 && recebidoEm - (amostras[0] as Amostra).t > JANELA_MS) amostras.shift();
  alvo = Math.max(...amostras.map((a) => a.diferenca));
  if (ultimaLeitura === 0) aplicada = alvo; // primeira amostra: já começa nela
}

/** Horário do servidor agora, suave e sem voltar atrás. */
export function agoraDoServidor(): number {
  const agora = Date.now();
  if (alvo === null) return agora;
  const dt = ultimaLeitura === 0 ? 0 : Math.min(1000, Math.max(0, agora - ultimaLeitura));
  ultimaLeitura = agora;
  const falta = alvo - aplicada;
  const passo = Math.sign(falta) * Math.min(Math.abs(falta), dt * VELOCIDADE_MAX_AJUSTE);
  aplicada += passo;
  ultimoValor = Math.max(ultimoValor, agora + aplicada);
  return ultimoValor;
}
